import { ButtonAsA } from 'components/Button/ButtonAsA';
import type { JSX, ReactElement } from 'react';

export const settingsWebHooks = {
  en: {
    heading: 'Webhooks',
    help: 'Webhooks enables sending notifications to web apps in response to events in the IP Fabric platform.',
    noDelivery: 'No delivery records for this webhook yet.',
    column: {
      active: 'Active',
      name: 'Name',
      eventTypes: 'Event types',
      action: 'Action',
      username: 'Username',
    },
    types: {
      sendAll: 'All',
      snapshot: 'Snapshot',
      'intent-verification': 'Intent verification',
    },
    yes: 'Yes',
    no: 'No',
    seeMore: 'See more',
    triggersDialog: {
      header: 'Triggers for',
      btnClose: 'Close',
      snapshotEvents: 'Snapshot events',
      intentEvents: 'Intent verification events',
      noSnapshotEvents: 'No snapshot events applied',
      noIntentEvents: 'No intent verification events applied',
    },
    btnAdd: 'Add webhook',
    btnEdit: 'Edit',
    btnDelete: 'Delete',
    btnClose: 'Close',
    btnTest: 'Test',
    btnDeleteHistory: 'Delete history',
    testHeading: 'Test your webhook',
    unknown: 'unknown',
    deliveries: {
      recentDeliveries: 'Recent deliveries',

      recentDeliveriesNote:
        'When an event in IP Fabric platform triggers a webhook, you can use the request details to figure out if something went wrong.',
      viewDetails: 'View details',
      column: {
        delivery: 'Delivery',
        retry: 'Retry',
        eventType: 'Event type',
        status: 'Status',
        action: 'Action',
      },
      deliveryDetail: 'Delivery detail',
      request: 'Request',
      body: 'Body:',
      hash: 'Hash:',
      id: 'Id:',
      retry: 'Retry:',
      time: 'Time:',
      url: 'URL:',
      response: 'Response',
      headers: 'Headers:',
      status: 'Status:',
      retryCount: 'retry',
      error: 'error',
      errorLabel: 'Error:',
    },
    form: {
      active: 'Active',
      name: 'Name',
      url: 'URL',
      caCert: 'CA certificate',
      caCertPlaceholder: 'Use appliance default trust',
      caCertUpload: 'Upload CA',
      caCertUploadTitle: 'Upload CA certificate',
      caCertUploadName: 'Name',
      caCertUploadSuccess: 'CA certificate uploaded.',
      caCertUploadError: 'Could not upload the CA certificate.',
      sendAll: 'Send me all events',
      selectIndividual: 'Select individual events',
      btnCreate: 'Create',
      btnSave: 'Save',
      btnCancel: 'Cancel',
      authenticationLabel: 'Authentication',
      token: 'Token',
      tokenHintNew:
        'Optional. Stored encrypted and sent with outbound deliveries.',
      tokenHintConfigured:
        'A token is configured. Leave blank to keep it, or enter a new value to replace it.',
      clearToken: 'Remove the stored token',
      useFineGrainedTriggers: 'Use manual trigger selection',
      triggersLabel: 'Which triggers would you like to fire this webhook?',
      eventsLabel: 'Which events would you like to trigger this webhook?',
      payloadFormat: 'Payload format',
      payloadTemplate: 'Payload template',
      availableVariables: 'Available variables',
      availableVariablesHint:
        'Reference with triple braces, e.g. {{{snapshot.status}}}. Exactly one event group is available per delivery.',
      variableGroupCommon: 'Common',
      variableGroupSnapshot: 'Snapshot events',
      variableGroupIntentCheck: 'Intent verification events',
      headersLabel: 'Custom headers',
      headersHint:
        'Header values support mustache templates. Available variables: {{{timestamp}}}, {{{user_id}}}, {{{token}}} (the configured outbound auth token).',
      headerKey: 'Header name',
      headerValue: 'Value',
      addHeader: 'Add header',
      validationMessages: {
        required: 'Required',
        invalidUrl: 'Invalid url',
        lengthMax64: 'Value should not be longer than 64 characters',
        atLeastOneEventType: 'Select at least one event type',
        atLeastOneTrigger: 'Select at least one trigger',
        headerInvalidName: (name: string): string =>
          `"${name}" is not a valid header name`,
        headerDuplicateName: (name: string): string =>
          `Duplicate header name "${name}"`,
        headerReservedName: (name: string): string =>
          `"${name}" is a reserved header name`,
        headerValueNewline: 'Header value must not contain line breaks',
        headerInvalidTemplate: 'Invalid template',
        headerUnsupportedVariable: (variable: string): string =>
          `Unsupported variable "${variable}"`,
      },
      type: 'Type',
      action: 'Action',
      status: 'Status',
      showHelp: 'Show help',
      closeHelp: 'Close help',
      // Rendered as function.

      secretNote: (): ReactElement => (
        <span>
          The secret code is used to calculate the SHA256 HMAC payload hash
          which is sent in
          <code>x-ipf-signature</code> header. Using{' '}
          <code>x-ipf-signature</code> you can validate that request comes from
          correct server.{' '}
          <a
            href='https://docs.ipfabric.io/latest/IP_Fabric_Settings/webhooks'
            target='_blank'
            rel='noreferrer'
          >
            See help for more info
          </a>
          .
        </span>
      ),
    },
    request: {
      created: 'Webhook created',
      saved: 'Webhook saved',
      deleted: 'Webhook deleted',
      deletedHistory: 'Delivery history deleted',
      // Rendered as function.

      test:
        (makeHandleClick: (handleClose: () => void) => () => void) =>
        (handleClose: () => void): JSX.Element => (
          <span>
            The test request is being processed,{' '}
            <ButtonAsA onClick={makeHandleClick(handleClose)}>
              click here
            </ButtonAsA>{' '}
            to see the result
          </span>
        ),
    },
    deleteConfirmDialog: {
      header: 'Webhook will be deleted',
      subHeader: 'Do you really want to delete the webhook?',
      btnConfirm: 'Delete',
      btnSecondaryConfirm: 'Cancel',
    },
    savedSecretDialog: {
      title: 'Secret',
      warning:
        "Use this secret as a password. Make sure you save it - you won't be able to access it again",
    },
  },
};
